/**
 * Card — eDMS Design System
 * Source: drafts/31_ux_design_system_spec.md §3.5
 *
 * Variants: default, interactive (clickable), elevated (shadow), outlined.
 * Composition: <Card><CardHeader/><CardBody/><CardFooter/></Card>.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './Card.module.css';

export type CardVariant = 'default' | 'interactive' | 'elevated' | 'outlined';

export interface CardProps extends HTMLAttributes<HTMLElement> {
  variant?: CardVariant;
  as?: 'div' | 'article' | 'section';
  children?: ReactNode;
}

export const Card = forwardRef<HTMLElement, CardProps>(
  ({ variant = 'default', as: Tag = 'section', className, children, ...rest }, ref) => {
    const Component = Tag as 'section';
    return (
      <Component
        ref={ref as React.Ref<HTMLElement>}
        className={clsx(styles.card, styles[variant], className)}
        {...rest}
      >
        {children}
      </Component>
    );
  }
);
Card.displayName = 'Card';

export function CardHeader({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <header className={clsx(styles.header, className)} {...rest}>
      {children}
    </header>
  );
}

export function CardBody({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={clsx(styles.body, className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <footer className={clsx(styles.footer, className)} {...rest}>
      {children}
    </footer>
  );
}
